import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Chip,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Alert,
  useTheme,
  alpha,
  Tooltip,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Settings,
  Notifications,
  Science,
  Assignment,
  ReportProblem,
  People,
  Description,
  Inventory,
  ExpandLess,
  ExpandMore,
  Dashboard as DashboardIcon,
  CheckCircle,
  SupervisorAccount,
  Analytics,
  Brightness4,
  Brightness7,
  TrendingUp,
  AutoAwesome,
  Tv as TvIcon,
  Assessment,
  FlashOn,
  LocalShipping,
  LocationOn,
  Hub,
  Verified,
  Scale,
  Sync,
  Biotech,
  ShowChart,
} from '@mui/icons-material';
import { useAuth, UserRole } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { isSatellite, hasCapability, LocationCapability } from '../../config/location.config';

const drawerWidth = 280;

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  roles: UserRole[];
  children?: NavigationItem[];
  badge?: number;
  color?: string;
  // Location capability required (if set, item only shows when capability is available)
  requiredCapability?: string;
  // Only show on main lab
  mainLabOnly?: boolean;
  // Only show on satellite
  satelliteOnly?: boolean;
}

const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Tableau de Bord',
    icon: <DashboardIcon />,
    path: '/dashboard',
    roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.ANALYST, UserRole.TECHNICIAN_PREP, UserRole.SAMPLE_RECEIVER],
    color: '#2563eb',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: <Analytics />,
    path: '/analytics',
    roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER],
    color: '#10b981',
  },
  {
    id: 'tv-dashboard',
    label: 'Écran TV',
    icon: <TvIcon />,
    path: '/tv-dashboard',
    roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER],
    color: '#8b5cf6',
    badge: 1,
  },
  {
    id: 'samples',
    label: 'Gestion Échantillons',
    icon: <Science />,
    roles: [UserRole.LABORATORY_MANAGER, UserRole.ANALYST, UserRole.TECHNICIAN_PREP, UserRole.SAMPLE_RECEIVER],
    color: '#7c3aed',
    children: [
      {
        id: 'samples-list',
        label: 'Liste Échantillons',
        icon: <Assignment />,
        path: '/samples',
        roles: [UserRole.LABORATORY_MANAGER, UserRole.ANALYST, UserRole.TECHNICIAN_PREP, UserRole.SAMPLE_RECEIVER],
        color: '#06b6d4',
      },
      {
        id: 'samples-reception',
        label: 'Réception',
        icon: <Inventory />,
        path: '/reception',
        roles: [UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER],
        color: '#10b981',
      },
    ],
  },
  {
    id: 'laboratories',
    label: 'Laboratoires',
    icon: <Science />,
    roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.ANALYST, UserRole.TECHNICIAN_PREP, UserRole.SAA_PREP_CHIMIQUE, UserRole.SAA_ANALYST, UserRole.ICP_PREP_CHIMIQUE, UserRole.ICP_ANALYST, UserRole.AU_PREP_CHIMIQUE, UserRole.AU_ANALYST, UserRole.VOLUMETRIE_PREP_CHIMIQUE, UserRole.VOLUMETRIE_ANALYST, UserRole.PHYSICO_CHIMIQUE_ANALYST, UserRole.PREPARATION_MECANIQUE, UserRole.VALIDATION_SERVICE],
    color: '#dc2626',
    children: [
      {
        id: 'lab-overview',
        label: 'Vue d\'ensemble',
        icon: <Settings />,
        path: '/laboratory',
        roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER],
        color: '#dc2626',
      },
      {
        id: 'lab-saa',
        label: 'SAA (Absorption Atomique)',
        icon: <AutoAwesome />,
        path: '/laboratory?lab=saa',
        roles: [UserRole.LABORATORY_MANAGER, UserRole.SAA_ANALYST, UserRole.SAA_PREP_CHIMIQUE],
        color: '#dc2626',
        mainLabOnly: true, // Labs only visible at main lab
      },
      {
        id: 'lab-icp',
        label: 'ICP (Spectro)',
        icon: <Science />,
        path: '/laboratory?lab=icp',
        roles: [UserRole.LABORATORY_MANAGER, UserRole.ICP_ANALYST, UserRole.ICP_PREP_CHIMIQUE],
        color: '#059669',
        mainLabOnly: true,
      },
      {
        id: 'lab-au',
        label: 'AU (Or)',
        icon: <AutoAwesome />,
        path: '/laboratory?lab=au',
        roles: [UserRole.LABORATORY_MANAGER, UserRole.AU_ANALYST, UserRole.AU_PREP_CHIMIQUE],
        color: '#d97706',
        mainLabOnly: true,
      },
      {
        id: 'lab-volumetrie',
        label: 'Volumétrie',
        icon: <Science />,
        path: '/laboratory?lab=volumetrie',
        roles: [UserRole.LABORATORY_MANAGER, UserRole.VOLUMETRIE_ANALYST, UserRole.VOLUMETRIE_PREP_CHIMIQUE],
        color: '#7c2d12',
        mainLabOnly: true,
      },
      {
        id: 'lab-physico-chimique',
        label: 'Physico-Chimique',
        icon: <Science />,
        path: '/physico-chimique/analyse',
        roles: [UserRole.LABORATORY_MANAGER, UserRole.PHYSICO_CHIMIQUE_ANALYST],
        color: '#059669',
        mainLabOnly: true,
      },
      {
        id: 'lab-physico-validation',
        label: 'Physico-Chimique (Validation)',
        icon: <Verified />,
        path: '/physico-chimique/validation',
        roles: [UserRole.LABORATORY_MANAGER, UserRole.QUALITY_MANAGER],
        color: '#047857',
        mainLabOnly: true,
      },
      {
        id: 'lab-preparation',
        label: 'Préparation Mécanique',
        icon: <Settings />,
        path: '/laboratory?lab=preparation',
        roles: [UserRole.LABORATORY_MANAGER, UserRole.PREPARATION_MECANIQUE],
        color: '#7c3aed',
      },
      {
        id: 'lab-validation',
        label: 'Service Validation',
        icon: <Settings />,
        path: '/laboratory?lab=validation',
        roles: [UserRole.LABORATORY_MANAGER, UserRole.VALIDATION_SERVICE],
        color: '#ea580c',
        mainLabOnly: true,
      },
    ],
  },
  {
    id: 'certificates',
    label: 'Certificats',
    icon: <Description />,
    path: '/results/certificates',
    roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.QUALITY_MANAGER],
    color: '#7c3aed',
    mainLabOnly: true, // Certificates only at main lab
  },
  {
    id: 'completed-samples',
    label: 'Échantillons Terminés',
    icon: <CheckCircle />,
    path: '/admin/completed-samples',
    roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER],
    color: '#16a34a',
  },
  {
    id: 'clients',
    label: 'Gestion Clients',
    icon: <People />,
    path: '/clients',
    roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER],
    color: '#06b6d4',
  },
  {
    id: 'users',
    label: 'Gestion Utilisateurs',
    icon: <SupervisorAccount />,
    path: '/users',
    roles: [UserRole.ADMIN],
    color: '#ef4444',
  },
  {
    id: 'methods',
    label: 'Gestion Méthodes',
    icon: <Science />,
    path: '/methods',
    roles: [UserRole.ADMIN],
    color: '#8b5cf6',
  },
  {
    id: 'reference-materials',
    label: 'Catalogue MRC / OREAS',
    icon: <Biotech />,
    path: '/admin/reference-materials',
    roles: [UserRole.ADMIN, UserRole.QUALITY_MANAGER],
    color: '#16a34a',
  },
  {
    id: 'qc-charts',
    label: 'Cartes de contrôle QA/QC',
    icon: <ShowChart />,
    path: '/admin/qc-charts',
    roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.QUALITY_MANAGER],
    color: '#0288d1',
  },
  {
    id: 'reclamations',
    label: 'Réclamations Certificats',
    icon: <ReportProblem />,
    path: '/admin/reclamations',
    roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.QUALITY_MANAGER],
    color: '#dc2626',
  },
  {
    id: 'multi-site',
    label: 'Multi-Site',
    icon: <Hub />,
    path: '/admin/multi-site',
    roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER],
    color: '#06b6d4',
  },
  {
    id: 'transfers',
    label: 'Transferts',
    icon: <LocalShipping />,
    path: '/transfers',
    roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER, UserRole.PREPARATION_MECANIQUE],
    color: '#f59e0b',
  },
  {
    id: 'locations',
    label: 'Gestion Sites',
    icon: <LocationOn />,
    path: '/admin/locations',
    roles: [UserRole.ADMIN],
    color: '#ec4899',
  },
  {
    id: 'scales',
    label: 'Gestion Balances',
    icon: <Scale />,
    path: '/admin/scales',
    roles: [UserRole.ADMIN],
    color: '#10b981',
  },
  {
    id: 'sync-config',
    label: 'Synchronisation',
    icon: <Sync />,
    path: '/admin/sync',
    roles: [UserRole.ADMIN],
    color: '#6366f1',
  },
  {
    id: 'audit',
    label: "Journal d'Audit",
    icon: <Assessment />,
    path: '/admin/audit',
    roles: [UserRole.ADMIN],
    color: '#ff9800',
  },
  {
    id: 'quick-actions',
    label: 'Actions Rapides',
    icon: <FlashOn />,
    path: '/admin/quick-actions',
    roles: [UserRole.ADMIN],
    color: '#ff5722',
  },
  {
    id: 'recherche-rapide',
    label: 'Recherche Rapide',
    icon: <TrendingUp />,
    path: '/admin/recherche-rapide',
    roles: [UserRole.ADMIN, UserRole.LABORATORY_MANAGER],
    color: '#06b6d4',
  },
  // Analytics entries removed to avoid duplication with Admin Dashboard statistics
];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // TV mode: hide chrome for TV dashboards
  const tvMode = location.pathname === '/tv-dashboard' || new URLSearchParams(location.search).get('mode') === 'tv';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>(['samples', 'laboratories']);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const handleItemClick = (item: NavigationItem) => {
    if (item.path) {
      navigate(item.path);
      setMobileOpen(false);
    } else if (item.children) {
      toggleExpanded(item.id);
    }
  };

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isItemActive = (path?: string) => {
    return path ? location.pathname === path : false;
  };

  const getFilteredNavigation = (items: NavigationItem[]): NavigationItem[] => {
    const isSatelliteLocation = isSatellite();
    
    return items.filter(item => {
      // Role check
      if (!user || !item.roles.includes(user.role)) {
        return false;
      }
      
      // Location capability check
      if (item.requiredCapability && !hasCapability(item.requiredCapability)) {
        return false;
      }
      
      // Main lab only items
      if (item.mainLabOnly && isSatelliteLocation) {
        return false;
      }
      
      // Satellite only items
      if (item.satelliteOnly && !isSatelliteLocation) {
        return false;
      }
      
      if (item.children) {
        const filteredChildren = getFilteredNavigation(item.children);
        return filteredChildren.length > 0;
      }
      
      return true;
    }).map(item => ({
      ...item,
      children: item.children ? getFilteredNavigation(item.children) : undefined
    }));
  };

  // Check if user has any navigation items (CLIENT_VIEWER should not see drawer)
  const hasNavigation = user && getFilteredNavigation(navigationItems).length > 0;

  const renderNavigationItem = (item: NavigationItem, level = 0) => {
    const isActive = isItemActive(item.path);
    const isExpanded = expandedItems.includes(item.id);
    const hasChildren = item.children && item.children.length > 0;

    return (
      <React.Fragment key={item.id}>
        <Zoom in timeout={300 + level * 100}>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleItemClick(item)}
              sx={{
                mx: 2,
                borderRadius: 3,
                minHeight: 48,
                pl: level * 2 + 2,
                pr: 2,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                background: isActive 
                  ? `linear-gradient(135deg, ${alpha(item.color || theme.palette.primary.main, 0.15)}, ${alpha(item.color || theme.palette.primary.main, 0.1)})`
                  : 'transparent',
                '&:hover': {
                  background: `linear-gradient(135deg, ${alpha(item.color || theme.palette.primary.main, 0.1)}, ${alpha(item.color || theme.palette.primary.main, 0.05)})`,
                  transform: 'translateX(4px)',
                  '& .nav-icon': {
                    transform: 'scale(1.1)',
                  },
                  '& .nav-text': {
                    fontWeight: 600,
                  },
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: isActive ? '4px' : '0px',
                  background: `linear-gradient(180deg, ${item.color || theme.palette.primary.main}, ${alpha(item.color || theme.palette.primary.main, 0.7)})`,
                  borderRadius: '0 2px 2px 0',
                  transition: 'width 0.3s ease',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Box
                  className="nav-icon"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    background: isActive 
                      ? `linear-gradient(135deg, ${item.color || theme.palette.primary.main}, ${alpha(item.color || theme.palette.primary.main, 0.8)})`
                      : alpha(item.color || theme.palette.primary.main, 0.1),
                    color: isActive ? 'white' : (item.color || theme.palette.primary.main),
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiSvgIcon-root': {
                      fontSize: 18,
                    },
                  }}
                >
                  {item.icon}
                </Box>
              </ListItemIcon>
              
              <ListItemText
                primary={item.label}
                className="nav-text"
                sx={{
                  '& .MuiTypography-root': {
                    fontSize: '0.875rem',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
                    transition: 'all 0.3s ease',
                  },
                }}
              />
              
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${item.color || theme.palette.primary.main}, ${alpha(item.color || theme.palette.primary.main, 0.8)})`,
                    color: 'white',
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              )}
              
              {hasChildren && (
                <Box
                  sx={{
                    ml: 1,
                    transition: 'transform 0.3s ease',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  <ExpandMore sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                </Box>
              )}
            </ListItemButton>
          </ListItem>
        </Zoom>
        
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children?.map(child => renderNavigationItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box sx={{ 
      height: '100%',
      background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
      backdropFilter: 'blur(20px)',
      borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    }}>
      {/* Logo Section */}
      <Fade in timeout={600}>
        <Box sx={{ 
          p: 3, 
          textAlign: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 2,
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 140, // Increased from 120 to 140 - NO MASK
                  height: 140, // Increased from 120 to 140 - NO MASK
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  mb: 1, // Small margin below logo
                }}
              >
                <img 
                  src="/logo2.png" 
                  alt="AFRILAB" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                  }} 
                />
              </Box>
              
              {/* Beautiful LIMS System text directly under logo */}
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  fontSize: '1.1rem',
                  letterSpacing: '0.02em',
                }}
              >
                LIMS System
              </Typography>
            </Box>
          </Box>
          
          {/* User Info */}
          <Box sx={{ 
            p: 2, 
            borderRadius: 3,
            background: alpha(theme.palette.background.paper, 0.7),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Chip
              label={user?.role.replace('_', ' ')}
              size="small"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                color: 'white',
              }}
            />
          </Box>
        </Box>
      </Fade>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 2 }}>
        <List>
          {getFilteredNavigation(navigationItems).map(item => renderNavigationItem(item))}
        </List>
      </Box>

      {/* Footer */}
      <Fade in timeout={1000}>
        <Box sx={{ 
          p: 2, 
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          background: alpha(theme.palette.background.paper, 0.5),
        }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: theme.palette.text.secondary,
              textAlign: 'center',
              display: 'block',
              fontWeight: 500,
            }}
          >
            © 2024 AFRILAB LIMS
          </Typography>
        </Box>
      </Fade>
    </Box>
  );

  if (tvMode) {
    // Minimal full-screen container for TV dashboard without AppBar/Drawer
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          overflow: 'hidden',
          bgcolor: theme.palette.background.default,
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Modern App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.98)})`,
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          color: theme.palette.text.primary,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* No mobile menu button needed since mobile drawer is completely removed */}
            
            <Typography 
              variant="h6" 
              noWrap 
              sx={{ 
                fontWeight: 600,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Laboratoire AFRILAB
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Notifications" arrow>
              <IconButton
                sx={{
                  background: alpha(theme.palette.warning.main, 0.1),
                  '&:hover': {
                    background: alpha(theme.palette.warning.main, 0.2),
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Badge badgeContent={4} color="error">
                  <Notifications sx={{ color: theme.palette.warning.main }} />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Profil utilisateur" arrow>
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{
                  background: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.2),
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                  }}
                >
                  {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            mt: 1,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            minWidth: 200,
          },
        }}
      >
        <MenuItem 
          onClick={handleProfileMenuClose}
          sx={{
            borderRadius: 2,
            mx: 1,
            my: 0.5,
            '&:hover': {
              background: alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          <ListItemIcon>
            <Settings sx={{ color: theme.palette.primary.main }} />
          </ListItemIcon>
          Paramètres
        </MenuItem>
        <MenuItem 
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            mx: 1,
            my: 0.5,
            '&:hover': {
              background: alpha(theme.palette.error.main, 0.1),
            },
          }}
        >
          <ListItemIcon>
            <Logout sx={{ color: theme.palette.error.main }} />
          </ListItemIcon>
          Déconnexion
        </MenuItem>
      </Menu>

      {/* Navigation Drawer - Only show on desktop (sm and up), never on mobile */}
      <Box
        component="nav"
        sx={{ 
          width: { sm: drawerWidth }, 
          flexShrink: { sm: 0 },
          display: { xs: 'none', sm: 'block' } // Hide entire drawer container on mobile
        }}
      >
        {/* Desktop drawer only */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              border: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { 
            xs: '100%', // Full width on mobile (no drawer)
            sm: `calc(100% - ${drawerWidth}px)` // Account for drawer on desktop
          },
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout; 